import React, { useCallback, useMemo } from 'react';
import type { Theme } from '@mentimeter/http-clients';
import { Icon } from '@mentimeter/question-modules-importer';
import {
  Thumbnail as SfinxThumbnail,
  ThumbnailProvider,
} from '@mentimeter/sfinx';
import { StringParser } from '@mentimeter/string-parser';
import { getQuestionLayout } from '@mentimeter/editor-essentials/calculations';
import { updateEditorSlideProxy } from '@mentimeter/compatibility/editor';
import { TextMarkupRenderer } from '@mentimeter/editor-ui';
import type { QuestionWithSlide } from '@mentimeter/editor-schema/api-types-overrides';
import { contrastColor } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import type { ThumbnailItem } from '../../../sfinx/src/setup/Thumbnail';
import { getThumbnailType } from './overviewListItemHelpers';

const THUMBNAIL_FONT_SIZE = 140;
const THUMBNAIL_FONT_SIZE_MOBILE = 120;

const MemoizedStringParser = React.memo(
  ({
    source,
    disableLineBreaks,
  }: Pick<
    React.ComponentProps<typeof StringParser>,
    'source' | 'disableLineBreaks'
  >) => (
    <StringParser
      source={source}
      disableLineBreaks={Boolean(disableLineBreaks)}
    />
  ),
);

export type OverviewListItemType = ThumbnailItem &
  Pick<
    QuestionWithSlide,
    | 'has_comments'
    | 'skip_slide'
    | 'question_styled'
    | 'question_description_styled'
    | 'question_description'
    | 'module_id'
    | 'question_image_url'
    | 'hasComments'
  >;

interface OverviewListItemThumbnailProps {
  smallText: boolean;
  theme: Theme;
  listItem: OverviewListItemType;
}

export const OverviewListItemThumbnail = ({
  smallText,
  theme,
  listItem,
}: OverviewListItemThumbnailProps) => {
  const question = useMemo(
    () =>
      updateEditorSlideProxy(listItem, {
        layout: { $set: getQuestionLayout(listItem) },
        question_styled: { $set: listItem.question_styled ?? {} },
        question_description_styled: {
          $set: listItem.question_description_styled ?? {},
        },
      }),
    [listItem],
  );

  const thumbnailType = getThumbnailType(
    question.image?.presets.small.url,
    question.slide_type,
    question.type,
  );

  const thumbnailStyling = useCallback(
    () => ({
      padding: thumbnailType.icon ? 0 : '0 36px',
    }),
    [thumbnailType.icon],
  );

  const titleId = `slide-thumbnail-title-${question.id}`;

  if (!thumbnailType.content) {
    return null;
  }

  const title =
    question.slide_type === 'free-text'
      ? question.question_description
      : question.question;

  const styledTitle =
    question.slide_type === 'free-text'
      ? question.question_description_styled
      : question.question_styled;

  const bgColor =
    question.theme_settings?.background_color || theme.background_color;
  const contrastTextColor = contrastColor(bgColor);

  return (
    <ThumbnailProvider
      config={{
        mode: 'idle',
        enableAnimations: false,
        enableHotkeys: false,
        baseUrl: '',
        votingUrl: '',
      }}
      presentationTheme={theme}
      thumbnailItem={question}
    >
      <SfinxThumbnail extend={thumbnailStyling} thumbnailItem={question}>
        <Box
          width="inherit"
          height="inherit"
          alignItems="inherit"
          justifyContent="inherit"
          extend={() => ({ gap: '3%' })}
        >
          <Box flex="0 1 auto" aria-hidden>
            {thumbnailType.icon && (
              <Icon
                moduleId={question.module_id}
                textColor={
                  question.override_theme && question.theme_settings?.text_color
                    ? question.theme_settings.text_color
                    : theme.text_color
                }
                size={400}
              />
            )}
          </Box>
          <Box flex="0 0 auto" maxWidth="100%">
            <Tooltip
              delayDuration={150}
              className="pointer-events-none max-w-[200px] max-h-[56px]"
              side="right"
              trigger={
                <Text
                  id={titleId}
                  truncate
                  color={contrastTextColor}
                  fontSize={
                    smallText ? THUMBNAIL_FONT_SIZE_MOBILE : THUMBNAIL_FONT_SIZE
                  }
                  extend={() => ({ pointerEvents: 'auto' })}
                >
                  <MemoizedStringParser source={title} disableLineBreaks />
                </Text>
              }
            >
              <Text
                color="inherit"
                fontSize="87.5"
                truncate
                extend={({}) => ({
                  display: '-webkit-box',
                  '-webkit-box-orient': 'vertical',
                  ' -webkit-line-clamp': 2,
                })}
              >
                {Object.keys(styledTitle ?? {}).length === 0 ? (
                  title
                ) : (
                  <TextMarkupRenderer content={styledTitle} />
                )}
              </Text>
            </Tooltip>
          </Box>
        </Box>
      </SfinxThumbnail>
    </ThumbnailProvider>
  );
};
